import supabase from '../supabaseClient';

/**
 * Finds teachers available to substitute for a given class and date range
 * @param {string} classId - The class that needs a substitute
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 * @param {string} originalTeacherId - The original teacher who needs to be substituted
 * @returns {Promise<Array>} Array of available teachers
 */
export const findAvailableSubstituteTeachers = async (classId, startDate, endDate, originalTeacherId) => {
  try {
    // First, get the class details including shift information
    const { data: classData, error: classError } = await supabase
      .from('classes')
      .select('id, name, shift, teacher_id')
      .eq('id', classId)
      .single();

    if (classError) throw classError;

    // Get all active teachers except the original teacher
    const { data: allTeachers, error: teachersError } = await supabase
      .from('teachers')
      .select('id, first_name, last_name, email')
      .eq('is_active', true)
      .neq('id', originalTeacherId);

    if (teachersError) throw teachersError;

    // Find teachers who are NOT assigned to classes during the same shift and date range
    const availableTeachers = [];

    for (const teacher of allTeachers) {
      const isAvailable = await isTeacherAvailable(
        teacher.id, 
        classData.shift, 
        startDate, 
        endDate,
        classId
      );
      
      if (isAvailable) {
        availableTeachers.push({
          ...teacher,
          availabilityReason: 'No conflicting assignments'
        });
      }
    }

    return availableTeachers;
  } catch (error) {
    console.error('Error finding available substitute teachers:', error);
    throw error;
  }
};

/**
 * Checks if a teacher is available for substitution during a specific period
 * @param {string} teacherId - Teacher to check
 * @param {string} shift - Shift type of the class needing substitution
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 * @param {string} excludeClassId - Class ID to exclude from conflict checking
 * @returns {Promise<boolean>} True if teacher is available
 */
export const isTeacherAvailable = async (teacherId, shift, startDate, endDate, excludeClassId = null) => {
  try {
    // Check if teacher has regular class assignments during this shift
    let classQuery = supabase
      .from('classes')
      .select('id, name, shift')
      .eq('teacher_id', teacherId)
      .eq('shift', shift);

    if (excludeClassId) {
      classQuery = classQuery.neq('id', excludeClassId);
    }

    const { data: conflictingClasses, error: classError } = await classQuery;
    
    if (classError) throw classError;

    // If teacher has regular classes in this shift, they're not available
    if (conflictingClasses && conflictingClasses.length > 0) {
      return false;
    }

    // Check if teacher is already assigned as a substitute during this period
    const { data: existingSubAssignments, error: subError } = await supabase
      .from('substitute_assignments')
      .select('id, class_id, start_date, end_date')
      .eq('substitute_teacher_id', teacherId)
      .in('status', ['pending', 'completed'])
      .or(`and(start_date.lte.${endDate},end_date.gte.${startDate})`);

    if (subError) throw subError;

    // If teacher has overlapping substitute assignments, they're not available
    if (existingSubAssignments && existingSubAssignments.length > 0) {
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error checking teacher availability:', error);
    return false;
  }
};

/**
 * Creates a new substitute assignment
 * @param {Object} assignmentData - The assignment details
 * @returns {Promise<Object>} The created assignment
 */
export const createSubstituteAssignment = async (assignmentData) => {
  try {
    // UI validation before sending to database
    if (!assignmentData.classId || !assignmentData.originalTeacherId || !assignmentData.substituteTeacherId) {
      throw new Error('Missing required assignment data');
    }
    
    if (assignmentData.originalTeacherId === assignmentData.substituteTeacherId) {
      throw new Error('Substitute teacher must be different from original teacher');
    }
    
    if (new Date(assignmentData.startDate) > new Date(assignmentData.endDate)) {
      throw new Error('End date must be on or after start date');
    }

    const { data, error } = await supabase
      .from('substitute_assignments')
      .insert([{
        class_id: assignmentData.classId,
        original_teacher_id: assignmentData.originalTeacherId,
        substitute_teacher_id: assignmentData.substituteTeacherId,
        start_date: assignmentData.startDate,
        end_date: assignmentData.endDate,
        reason: assignmentData.reason || null,
        notes: assignmentData.notes || null,
        status: 'pending', // Start as pending, admin can mark completed later
        created_by: assignmentData.createdBy
      }])
      .select(`
        *,
        classes:class_id (id, name, shift),
        original_teacher:original_teacher_id (id, first_name, last_name),
        substitute_teacher:substitute_teacher_id (id, first_name, last_name)
      `)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating substitute assignment:', error);
    throw error;
  }
};

/**
 * Updates a substitute assignment
 * @param {string} assignmentId - The assignment ID to update
 * @param {Object} updateData - The data to update
 * @returns {Promise<Object>} The updated assignment
 */
export const updateSubstituteAssignment = async (assignmentId, updateData) => {
  try {
    // UI validation for status updates
    if (updateData.status && !['pending', 'completed', 'cancelled'].includes(updateData.status)) {
      throw new Error('Invalid status. Must be pending, completed, or cancelled');
    }
    
    // Date validation if dates are being updated
    if (updateData.start_date && updateData.end_date && new Date(updateData.start_date) > new Date(updateData.end_date)) {
      throw new Error('End date must be on or after start date');
    }

    const { data, error } = await supabase
      .from('substitute_assignments')
      .update({
        ...updateData,
        updated_at: new Date().toISOString()
      })
      .eq('id', assignmentId)
      .select(`
        *,
        classes:class_id (id, name, shift),
        original_teacher:original_teacher_id (id, first_name, last_name),
        substitute_teacher:substitute_teacher_id (id, first_name, last_name)
      `)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating substitute assignment:', error);
    throw error;
  }
};

/**
 * Gets substitute assignments for a specific date range
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 * @param {Object} filters - Additional filters (classId, teacherId, status)
 * @returns {Promise<Array>} Array of substitute assignments
 */
export const getSubstituteAssignments = async (startDate, endDate, filters = {}) => {
  try {
    let query = supabase
      .from('substitute_assignments')
      .select(`
        *,
        classes:class_id (id, name, shift),
        original_teacher:original_teacher_id (id, first_name, last_name),
        substitute_teacher:substitute_teacher_id (id, first_name, last_name)
      `)
      .or(`and(start_date.lte.${endDate},end_date.gte.${startDate})`)
      .order('start_date', { ascending: true });

    if (filters.classId) {
      query = query.eq('class_id', filters.classId);
    }

    if (filters.teacherId) {
      query = query.or(`original_teacher_id.eq.${filters.teacherId},substitute_teacher_id.eq.${filters.teacherId}`);
    }

    if (filters.status) {
      query = query.eq('status', filters.status);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error getting substitute assignments:', error);
    throw error;
  }
};

/**
 * Gets the effective teacher for a class on a specific date
 * Takes into account substitute assignments
 * @param {string} classId - The class ID
 * @param {string} date - Date in YYYY-MM-DD format
 * @returns {Promise<Object>} Teacher object (original or substitute)
 */
export const getEffectiveTeacher = async (classId, date) => {
  try {
    // First get the original teacher for the class
    const { data: classData, error: classError } = await supabase
      .from('classes')
      .select(`
        id,
        name,
        teacher_id,
        teachers:teacher_id (id, first_name, last_name)
      `)
      .eq('id', classId)
      .single();

    if (classError) throw classError;

    // Check if there's an active substitute assignment for this date
    const { data: substituteAssignment, error: subError } = await supabase
      .from('substitute_assignments')
      .select(`
        *,
        substitute_teacher:substitute_teacher_id (id, first_name, last_name)
      `)
      .eq('class_id', classId)
      .lte('start_date', date)
      .gte('end_date', date)
      .eq('status', 'pending') // Only pending assignments are "active"
      .maybeSingle();

    if (subError) throw subError;

    // Return substitute teacher if assignment exists, otherwise original teacher
    if (substituteAssignment) {
      return {
        ...substituteAssignment.substitute_teacher,
        isSubstitute: true,
        assignmentId: substituteAssignment.id,
        substituteReason: substituteAssignment.reason
      };
    }

    return {
      ...classData.teachers,
      isSubstitute: false
    };
  } catch (error) {
    console.error('Error getting effective teacher:', error);
    throw error;
  }
};

/**
 * Cancels a substitute assignment
 * @param {string} assignmentId - The assignment ID to cancel
 * @param {string} reason - Reason for cancellation
 * @returns {Promise<Object>} The cancelled assignment
 */
export const cancelSubstituteAssignment = async (assignmentId, reason = null) => {
  try {
    const { data, error } = await supabase
      .from('substitute_assignments')
      .update({
        status: 'cancelled',
        notes: reason ? `Cancelled: ${reason}` : 'Cancelled',
        updated_at: new Date().toISOString()
      })
      .eq('id', assignmentId)
      .select(`
        *,
        classes:class_id (id, name, shift),
        original_teacher:original_teacher_id (id, first_name, last_name),
        substitute_teacher:substitute_teacher_id (id, first_name, last_name)
      `)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error cancelling substitute assignment:', error);
    throw error;
  }
};
